// Frontend gating for HSP Manager `hsp:*` permissions.
//
// Identity comes from Emergent Managed Google Auth, but the ROLE and
// PERMISSIONS list are Mongo-owned and returned by `/api/auth/me`. We
// mirror them into the module-level `auth.me()` cache in AuthProvider.

import React from "react";
import { auth, subscribeCurrentUser } from "./api";

function useCurrentUser() {
  const [me, setMe] = React.useState(auth.me());
  React.useEffect(() => subscribeCurrentUser(setMe), []);
  return me;
}

/** Returns { has, isSuperAdmin, ready }. `has(key)` checks the current
 *  user's permission list from /api/auth/me. Super-admin always
 *  resolves true. */
export function usePermissions() {
  const me = useCurrentUser();
  const perms = React.useMemo(() => new Set(me?.permissions || []), [me]);
  const isSuperAdmin = me?.role === "SUPER_ADMIN";

  const check = React.useCallback(
    (key) => {
      if (!key) return true;
      if (isSuperAdmin) return true;
      return perms.has(key);
    },
    [perms, isSuperAdmin],
  );

  return { has: check, isSuperAdmin, ready: !!me };
}

/** Render children only if the current user carries `permission`. */
export function Can({ permission, fallback = null, children }) {
  const { has, ready } = usePermissions();
  if (!ready) return null;
  return has(permission) ? <>{children}</> : fallback;
}
